import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, AlertTriangle } from 'lucide-react';
import api from '../../services/api';
import { logger } from '../../lib/logger';

/**
 * Tiny pill that polls the data-quality summary endpoint on mount.
 * Green "All clean" if zero issues, amber "X issues" with click-through
 * to /data-quality otherwise. Renders nothing while loading so it never
 * jiggles the header layout.
 */
const DataQualityBadge = () => {
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/data-quality/program-code-issues/summary');
        setSummary(res.data);
      } catch (err) {
        // Badge stays hidden on error so it never blocks the page. Log
        // the failure so we can investigate flaky endpoints in dev tools
        // without surfacing a noisy toast to the user.
        logger.warn('DataQualityBadge summary fetch failed', err);
      }
    })();
  }, []);

  if (!summary) return null;
  const total = summary.grand_total_issues;

  if (total === 0) {
    return (
      <Link
        to="/data-quality"
        className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50/60 px-3 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-100 transition-colors"
        data-testid="data-quality-badge"
        title="No program/code mismatches on patients or claims"
      >
        <ShieldCheck className="h-3.5 w-3.5" />
        Data clean
      </Link>
    );
  }
  return (
    <Link
      to="/data-quality"
      className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-800 hover:bg-amber-100 transition-colors"
      data-testid="data-quality-badge"
      title="Click to see and fix program/code mismatches"
    >
      <AlertTriangle className="h-3.5 w-3.5" />
      {total} data {total === 1 ? 'issue' : 'issues'} — fix
    </Link>
  );
};

export default DataQualityBadge;
